import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

const components: Components = {
  p: ({ node, ...props }) => (
    <p className="my-1 first:mt-0 last:mb-0 leading-relaxed" {...props} />
  ),
  strong: ({ node, ...props }) => (
    <strong className="font-semibold" {...props} />
  ),
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  h1: ({ node, ...props }) => (
    <h1 className="text-base font-semibold mt-2 mb-1 first:mt-0" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-base font-semibold mt-2 mb-1 first:mt-0" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-sm font-semibold mt-2 mb-1 first:mt-0" {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4 className="text-sm font-semibold mt-2 mb-1 first:mt-0" {...props} />
  ),
  h5: ({ node, ...props }) => (
    <h5 className="text-sm font-semibold mt-2 mb-1 first:mt-0" {...props} />
  ),
  h6: ({ node, ...props }) => (
    <h6 className="text-sm font-semibold mt-2 mb-1 first:mt-0" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul
      className="list-disc pl-5 my-1 space-y-0.5 [&_ul]:list-[circle] [&_ul_ul]:list-[square]"
      {...props}
    />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-5 my-1 space-y-0.5" {...props} />
  ),
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="underline underline-offset-2 text-primary hover:opacity-80 break-words"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  code: ({ node, className, children, ...props }) => {
    const isBlock = typeof className === "string" && /\blanguage-/.test(className);
    if (!isBlock) {
      return (
        <code
          className="rounded bg-black/10 dark:bg-white/10 px-1 py-0.5 text-[0.85em] font-mono"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code className={`font-mono text-[0.85em] ${className || ""}`} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ node, ...props }) => (
    <pre
      className="my-2 rounded-md bg-black/10 dark:bg-white/10 p-2 overflow-x-auto text-[0.85em]"
      {...props}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-1 border-l-2 border-border pl-3 italic opacity-90"
      {...props}
    />
  ),
  hr: ({ node, ...props }) => (
    <hr className="my-2 border-border" {...props} />
  ),
  table: ({ node, ...props }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full text-[0.9em] border-collapse" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th
      className="border border-border px-2 py-1 text-left font-semibold"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-border px-2 py-1" {...props} />
  ),
};

interface MarkdownMessageProps {
  content: string;
  className?: string;
}

export function MarkdownMessage({ content, className }: MarkdownMessageProps) {
  return (
    <div className={`break-words ${className || ""}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownMessage;
